import { entityReport } from '@api/api/entity-report';
import { MappedEntity } from '@api/api/entity-schema';
import { DisplayFormat } from '@api/api/timeseries-schema';
import { listSchema, watchlist } from '@api/api/watchlist';
import { ActionModal } from '@terminal/components/actions-modal/ActionsModal';
import {
  SharedFilterState,
  WatchlistFilterGroupKeys,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { ApiFetchResponse } from '@terminal/utils/api-fetch/apiFetch';
import { WretchError } from 'wretch/resolver';
import { z } from 'zod';

export type LatestValues = z.infer<
  typeof entityReport.getEntityLatestValues.schema
>;
export type Watchlists = z.infer<typeof watchlist.getListsByEntities.schema>;
export type LegacyWatchlists = z.infer<typeof watchlist.getLegacyLists.schema>;
export type SubscribedWatchlists = z.infer<typeof watchlist.getLists.schema>;

export type ReturnValue = {
  value: number;
  formattedValue: string;
  lastUpdate?: string;
};

export type EntityReturns = {
  rsi?: ReturnValue;
  liveReturn1D?: {
    value?: number;
    formattedValue?: string;
    lastUpdate?: string;
  };
  return1D?: ReturnValue;
  return1W?: ReturnValue;
  return1M?: ReturnValue;
  return3M?: ReturnValue;
  return6M?: ReturnValue;
  return1Y?: ReturnValue;
  peTrailing?: ReturnValue;
  peForward?: ReturnValue;
};

export type WatchlistEntities = {
  tag: string;
  asset?: MappedEntity;
  returns?: EntityReturns;
  prices?: {
    last_value: number;
    before_last_value: number;
  };
  displayFormat?: DisplayFormat;
  isLoading?: boolean;
};

export interface List extends z.infer<typeof listSchema> {}

export interface Watchlist
  extends z.infer<typeof watchlist.getLists.schema.element> {
  isFavorite: boolean;
  entities: WatchlistEntities[];
}

export type WatchlistEntity = z.infer<typeof watchlist.postListEntities.schema>;

export type WatchlistStore = {
  activeListId: string | undefined;
  lists: Watchlist[];
  error: WretchError | undefined;
  listIdsByTag: Map<string, string[]>;
  toggleFavoriteWatchlist: (id: string) => Promise<void>;
  getLists: () => Promise<void>;
  getList: (id: string) => Promise<void>;
  setListSubscription: (id: string, subscribed: boolean) => Promise<void>;
  createList: (
    name: string,
    tags: string[],
    unresolvedTags?: string[]
  ) => Promise<string | null>;
  createListLoading: boolean;
  updateList: ({
    id,
    name,
    tags,
  }: {
    id: string;
    name: string;
    tags: string[];
  }) => Promise<ApiFetchResponse<List>>;
  updateListName: (id: string, name: string) => Promise<ApiFetchResponse<List>>;
  deleteList: (id: string) => Promise<void>;
  addItemToList: (id: string, tag: string) => Promise<void>;
  removeItemFromList: (id: string, tag: string) => Promise<void>;
  isInAnyWatchlists: (tag: string) => Promise<Watchlists>;
  isInWatchlist: (tag: string, watchlistId: string) => Promise<boolean>;
  hasListWithName: (name: string) => boolean;
  createListByAsset: (
    assetTag: string,
    newListName: string
  ) => Promise<string | null>;
  editListByAsset: (
    id: string,
    assetTag: string
  ) => Promise<[[] | string[], string[]]>;
  setActiveListId: (id?: string) => void;
  activeModal?: Extract<ActionModal, 'rename' | 'delete'>;
  setActiveModal: (props: {
    activeModal?: Extract<ActionModal, 'rename' | 'delete'>;
  }) => void;
  resetFilterGroups: () => void;
} & SharedFilterState<WatchlistFilterGroupKeys>;

export enum WatchlistError {
  Duplicate = 'duplicate',
  EmptyName = 'emptyName',
  EmptyList = 'emptyList',
  ServerError = 'serverError',
  UsageLimitExceeded = 'usageLimitExceeded',
}
